import { TitleButton } from '@/common/buttons/TitleButton';
import { KrCard } from '@/common/card/KrCard';
import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { GaugeChart } from '@/common/dashboard/charts/GaugeChart';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { StatCard } from '@/common/dashboard/StatCard';
import { DimTree } from '@/common/entityFramework/entityPerspective/support/DimTree';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { Form } from '@/common/form/Form';
import { useFormFieldDef } from '@/common/form/hooks/useFormFieldDef';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { CumulativeSalesChart } from './CumulativeSalesChart';

// const PROG_OPTION_LABEL = 'AES REGINALD RICE JR_3 (S)(C) Sales Program';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDate = (date, opts) => {
  const str = String(date ?? '');
  if (str.length !== 8) {
    return '';
  }
  return new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`).toLocaleDateString('en-us', opts);
};

const BASIS_LABELS = {
  NET_SALES: 'Net Sales',
  INCR_NET_SALES: 'Incremental Net Sales',
  GROSS_SALES: 'Gross Sales',
  QTY: 'Units',
};

// Singular form, since a contract price is stated per one unit of its basis
const BASIS_UNIT_LABELS = { ...BASIS_LABELS, QTY: 'Unit' };

// A contract price stated against a payment basis is a rate per unit of it; with no basis it is the rebate itself
const basisSuffix = ({ isCtrtPrice, pymtBasis } = {}) => {
  if (!pymtBasis) {
    return null;
  }
  return isCtrtPrice ?
      `per ${BASIS_UNIT_LABELS[pymtBasis] ?? pymtBasis}`
    : `of ${BASIS_LABELS[pymtBasis] ?? pymtBasis}`;
};

// Concession is stored as a fraction for percent-based Calc Types (0.05 = 5%)
const formatConc = (conc, isPct) => {
  const value = Number(conc);
  if (!Number.isFinite(value)) {
    return null;
  }
  return isPct ?
      `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
    : `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};

// Program Products are grouped under the one whose Tier formula measures them: selecting a member under a
// different group than the current selection replaces the selection rather than adding to it.
const ProgProdsTreeField = ({ fieldDef }) => {
  const { value, setValue, label, disabled, onBlur, error, helperText } = useFormFieldDef(fieldDef);

  const memberIdToGroupId = new Map();
  (fieldDef.treeData?.children ?? []).forEach((node) => {
    if (node.children?.length) {
      node.children.forEach((memberNode) => memberIdToGroupId.set(memberNode.id, node.id));
    } else {
      memberIdToGroupId.set(node.id, 'root');
    }
  });

  const handleFilterChange = (newSelectedIds) => {
    const prevSelectedIds = new Set(value ?? []);
    const addedIds = newSelectedIds.filter((id) => !prevSelectedIds.has(id));
    if (addedIds.length === 0) {
      setValue(newSelectedIds);
      return;
    }
    const targetGroupId = memberIdToGroupId.get(addedIds[0]);
    setValue(newSelectedIds.filter((id) => memberIdToGroupId.get(id) === targetGroupId));
  };

  // The tree is rebuilt (with new node ids) whenever the selected Program changes, so a persisted
  // selection from a prior tree shape can reference ids that no longer exist.
  const validSelectedIds = (value ?? []).filter((id) => memberIdToGroupId.has(id));

  return (
    <DimTree
      displayName={label}
      disabled={disabled}
      treeData={fieldDef.treeData}
      selectedIds={validSelectedIds}
      onFilterChange={handleFilterChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
    />
  );
};

export const ProgressChart = ({ date, custId, progProdId, memberProgProdIds, thresholdKey }) => {
  const rsrc = useRsrc();
  const { navigateTo } = useNavState();
  const [progressData, setProgressData] = useState();

  useEffect(() => {
    if (!date || !custId || !progProdId || !memberProgProdIds?.length) {
      setProgressData(undefined);
      return;
    }

    rsrc
      .data('md', 'TIER_SALE_PROGRESS_CHART_DATA_PLUGIN', { date, custId, progProdId, memberProgProdIds, thresholdKey })
      .then((res) => {
        setProgressData({
          salesAmt: toNumber(res.salesAmt, 0),
          tierValues: res.tierValues ?? [],
          tierDetails: res.tierDetails ?? [],
          tier: res.tier,
          conc: res.conc,
          concIsPct: res.concIsPct,
        });
      });
  }, [date, custId, progProdId, memberProgProdIds?.join(','), thresholdKey]);

  const salesAmt = toNumber(progressData?.salesAmt, 0);
  const tierValuesRaw = progressData?.tierValues ?? [];
  const tierLowerBounds = (Array.isArray(tierValuesRaw) ? tierValuesRaw.flat() : [])
    .map((v) => toNumber(v, NaN))
    .filter((v) => Number.isFinite(v))
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);

  // A negative first bound (for example -1) is a sentinel for Tier 0 and should not be rendered as a chart demarcation.
  const tierValues = tierLowerBounds.filter((v) => v >= 0);
  const hiddenTierCount = tierLowerBounds.length - tierValues.length;

  // No explicit max is provided by backend, so keep the gauge readable by padding beyond the largest of sales/tier top.
  const topTier = tierValues.length ? tierValues[tierValues.length - 1] : 0;
  let gaugeMax = Math.max(1, Math.ceil(Math.max(topTier * 1.2, salesAmt * 1.1)));
  // A ladder with no renderable threshold (an [ACCESS]-only Tier, or one whose bounds are all sentinels) has
  // nothing to measure progress against, so the gauge just reads out the sales amount on an arbitrary scale.
  const noThresholdMode = tierValues.length === 0;
  const gaugeValue = noThresholdMode ? gaugeMax : salesAmt;
  const tierSplits = tierValues;

  if (noThresholdMode) {
    gaugeMax = Math.max(1, salesAmt * 2);
  }

  // A Tier's non-sales gates (LOYALTY_PCT) are not what this dashboard tracks, so the Tier and concession
  // are earned off the sales thresholds alone and can sit above the one the price evaluation resolved.
  let salesTierIndex = 0;
  if (noThresholdMode) {
    salesTierIndex = Math.max(0, tierLowerBounds.length - 1);
  } else {
    for (let i = 0; i < tierLowerBounds.length; i++) {
      if (salesAmt >= tierLowerBounds[i]) {
        salesTierIndex = i;
      }
    }
  }
  const currentTier = `Tier ${progressData?.tier ?? salesTierIndex}`;

  const concLabel = formatConc(progressData?.conc, progressData?.concIsPct);

  const dataLabelFormatter = function () {
    const amount = Number(salesAmt);
    const formatted = Number.isFinite(amount) ? amount.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0';
    const tierLine = [currentTier, concLabel].filter(Boolean).join(' — ');
    return `<span style="font-size:16px">$${formatted}</span><br/><span style="font-size:16px">${tierLine}</span>`;
  };

  // A band belongs to the highest Tier its lower bound has already cleared - bounds, not band index,
  // because the gauge drops duplicate and out-of-range thresholds when it lays the bands out.
  const tierDetails = progressData?.tierDetails ?? [];

  const detailForBound = (lowerBound) => {
    const reached = tierDetails.filter((detail) => toNumber(detail.threshold, 0) <= lowerBound);
    return reached.length ? reached[reached.length - 1] : tierDetails[0];
  };

  const tierTooltipFormatter = (tierNumber, lowerBound) => {
    const detail = detailForBound(lowerBound);
    if (!detail) {
      return null;
    }
    const conc = formatConc(detail.conc, detail.concIsPct);
    const threshold = toNumber(detail.threshold, NaN);
    const cap = detail.cap == null ? NaN : toNumber(detail.cap, NaN);
    return [
      `Tier ${detail.tier ?? tierNumber}`,
      conc && `Concession: ${[conc, basisSuffix(detail)].filter(Boolean).join(' ')}`,
      detail.calcType && `Calc Type: ${detail.calcType}`,
      threshold >= 0 && `Threshold: $${threshold.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      // The most the Tier can pay in a period, stated only by the Tiers that limit it
      Number.isFinite(cap) && `Cap: $${cap.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      detail.formula && `Qualifies When: ${detail.formula}`,
    ].filter(Boolean);
  };

  const navigateToTier = (tierNumber, lowerBound, event) => {
    const tierId = detailForBound(lowerBound)?.id;
    if (!tierId) {
      return;
    }
    navigateTo('/md/prog-prod-tier', { mode: Mode.DETAIL, entityId: tierId }, event?.ctrlKey);
  };

  return (
    <>
      <GaugeChart
        value={gaugeValue}
        max={gaugeMax}
        thresholds={tierValues}
        tierSplits={tierSplits}
        tierLabelOffset={hiddenTierCount}
        thresholdColors={noThresholdMode ? ['#55BF3B'] : undefined}
        title="Tier Period Eligible Sales"
        dataLabelFormatter={dataLabelFormatter}
        tierTooltipFormatter={tierTooltipFormatter}
        onTierClick={navigateToTier}
      />
    </>
  );
};

// The rebate is paid on the Program Product the selection is grouped under, at the concession of the Tier its
// Cust has earned - a Tier is earned by clearing every bar its formula states, so neither which bar is being
// charted nor which members are selected has any say in what the cards report.
export const RebateCards = ({ date, custId, progProdId, showProjected }) => {
  const rsrc = useRsrc();
  const [data, setData] = useState();

  useEffect(() => {
    if (!date || !custId || !progProdId) {
      setData(undefined);
      return;
    }

    rsrc.data('md', 'TIER_REBATE_CARDS_DATA_PLUGIN', { date, custId, progProdId }).then((res) => {
      setData({
        ...res,
        rebateAmt: toNumber(res.rebateAmt, 0),
        projRebateAmt: toNumber(res.projRebateAmt, 0),
        basisAmt: toNumber(res.basisAmt, 0),
        projBasisAmt: toNumber(res.projBasisAmt, 0),
        capAmt: res.capAmt == null ? null : toNumber(res.capAmt, null),
        projCapAmt: res.projCapAmt == null ? null : toNumber(res.projCapAmt, null),
      });
    });
  }, [date, custId, progProdId]);

  // A per-unit rate is only as meaningful as the volume it is earned on, so the units ride along with it
  const qtyLabel = (basisAmt) =>
    data?.pymtBasis === 'QTY' ?
      `\u00d7 ${basisAmt.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${BASIS_LABELS.QTY}`
    : null;

  // Only reported when the Tier's cap is what the amount landed on, since that is why it reads below the rate
  const capLabel = (capAmt) =>
    capAmt == null ? null : `(capped at $${capAmt.toLocaleString('en-US', { maximumFractionDigits: 0 })})`;

  // The cap note gets its own line so the rate it qualifies stays readable at full width
  const rateLabel = (tier, conc, basisAmt, capAmt) => {
    const concLabel = formatConc(conc, data?.concIsPct);
    const rate = concLabel && [concLabel, basisSuffix(data), qtyLabel(basisAmt)].filter(Boolean).join(' ');
    return [[tier == null ? null : `Tier ${tier}`, rate].filter(Boolean).join(' — '), capLabel(capAmt)].filter(Boolean);
  };

  const periodEnd = formatDate(data?.endDate, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Spacer vertical>
      <StatCard
        title="Current Rebate"
        val={data?.supported ? data.rebateAmt : null}
        isAmt
        label={
          data?.supported ? rateLabel(data.tier, data.conc, data.basisAmt, data.capAmt) : 'Payment basis not supported'
        }
        center={!showProjected}
      />
      {showProjected && (
        <StatCard
          title="Projected Rebate"
          val={data?.supported ? data.projRebateAmt : null}
          isAmt
          label={
            data?.supported ?
              rateLabel(data.projTier, data.projConc, data.projBasisAmt, data.projCapAmt)
            : 'Payment basis not supported'
          }
          vsLabel={periodEnd}
        />
      )}
    </Spacer>
  );
};

export const CumulativeSaleChart = ({ date, custId, progProdId, memberProgProdIds, thresholdKey, markerDate }) => {
  const rsrc = useRsrc();
  const [data, setData] = useState();

  useEffect(() => {
    if (!date || !custId || !progProdId || !memberProgProdIds?.length) {
      setData(undefined);
      return;
    }

    rsrc
      .data('md', 'TIER_SALE_CUMULATIVE_CHART_DATA_PLUGIN', {
        date,
        custId,
        progProdId,
        memberProgProdIds,
        thresholdKey,
      })
      .then((res) => {
        setData({
          categories: res.categories,
          priorCategories: res.priorCategories,
          series: (res.series ?? []).map((series) => ({
            ...series,
            values: (series.values ?? []).map((value) => toNumber(value, 0)),
          })),
          tierValues: res.tierValues ?? [],
          startDate: res.startDate,
        });
      });
  }, [date, custId, progProdId, memberProgProdIds?.join(','), thresholdKey]);

  const tierValuesRaw = data?.tierValues ?? [];
  const tierLowerBounds = (Array.isArray(tierValuesRaw) ? tierValuesRaw.flat() : [])
    .map((v) => toNumber(v, NaN))
    .filter((v) => Number.isFinite(v))
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);

  const tierValues = tierLowerBounds.filter((v) => v >= 0);
  const hiddenTierCount = tierLowerBounds.length - tierValues.length;

  return (
    <>
      {data && (
        <CumulativeSalesChart
          categories={data.categories}
          priorCategories={data.priorCategories}
          series={data.series}
          tierValues={tierValues}
          tierLabelOffset={hiddenTierCount}
          startDate={data.startDate}
          markerDate={markerDate}
        />
      )}
    </>
  );
};

export const SaleChart = ({ date, custId, progProdId, members, thresholdKey }) => {
  const rsrc = useRsrc();
  const { navigateTo } = useNavState();

  const [data, setData] = useState({ categories: [], monthCodes: [], series: [] });

  useEffect(() => {
    if (!date || !progProdId || !members?.length) {
      setData({ categories: [], monthCodes: [], series: [] });
      return;
    }

    Promise.all(
      members.map((member) =>
        rsrc.data('md', 'TIER_SALE_TREND_CHART_DATA_PLUGIN', {
          date,
          custId,
          progProdId,
          memberProgProdIds: [member.id],
          thresholdKey,
        }),
      ),
    ).then((results) => {
      setData({
        categories: results[0].categories,
        monthCodes: results[0].monthCodes ?? [],
        series: results.map((r, i) => ({ ...r.series[0], label: members[i].name })),
      });
    });
  }, [date, custId, progProdId, members?.map((member) => member.id).join(','), thresholdKey]);

  const tooltipFormatter = ({ category, points }) => {
    const lines = points.map(
      (p) =>
        `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>$${Number(p.y).toLocaleString('en-US', { maximumFractionDigits: 0 })}</b>`,
    );
    return `<b>${category}</b><br/>${lines.join('<br/>')}`;
  };

  // Categories are display labels, so the drill-down uses the parallel YYYYMM codes the plugin returns.
  // A Line is only tied to a Program Product through the Dir Sale Line Prog Prod matches, so the matched
  // Line ids are resolved first and then used to filter the Sale Lines.
  const navigateToDirSaleLineList = async (point) => {
    const monthCode = data.monthCodes[point.x];
    const member = members?.[point.series?.index];
    if (!monthCode || !member) {
      return;
    }

    const matches = await rsrc.rows('sc', 'DIR_SALE_LINE_PROG_PROD', {
      expr: ExprUtils.and(
        ExprUtils.is('progProdId', member.id),
        custId ? ExprUtils.is('custId', custId) : null,
        ExprUtils.effectiveDateClause('earnedDate', Number(`${monthCode}01`), Number(`${monthCode}31`)),
      ),
    });
    if (!matches.length) {
      return;
    }

    const expr = ExprUtils.and(
      ExprUtils.setExpr(
        'id',
        matches.map((match) => match.dirSaleLineId),
      ),
      // Only validated Lines are matched to a Program Product, so only those contribute to the charts
      ExprUtils.is('valStatus', 'VALID'),
    );
    navigateTo('/sc/dir-sale-line', { mode: Mode.LIST, expr });
  };

  return (
    <>
      <CartesianChart
        data={data}
        yLabel={'Total Eligible Sales ($)'}
        xLabel="Invoice Month"
        allowNegative
        stacked
        legend={members?.length > 1 ? 'top' : undefined}
        tooltipFormatter={tooltipFormatter}
        onClick={navigateToDirSaleLineList}
      />
    </>
  );
};

const progProdsFieldDef = (progProdTreeData, disabled) => {
  const fieldDef = {
    field: 'progProdIds',
    displayName: 'Products',
    type: 'TREE',
    treeData: progProdTreeData,
    disabled,
  };
  fieldDef.component = <ProgProdsTreeField fieldDef={fieldDef} />;
  return fieldDef;
};

// The Products field renders through a component bound to its own fieldDef, so it has to be rebuilt rather than spread
const disabledFieldDef = (fieldDef) =>
  fieldDef.field === 'progProdIds' ? progProdsFieldDef(fieldDef.treeData, true) : { ...fieldDef, disabled: true };

// Periods arrive newest first, so the first one that has already closed is the most recent past period.
const defaultPeriodValue = (periods, today) => {
  const current = periods.find((period) => period.value <= today && today <= period.endDate);
  const past = periods.find((period) => period.endDate < today);
  return (current ?? past ?? periods[0]).value;
};

export const TierDashboard = () => {
  const rsrc = useRsrc();
  const { navigateTo } = useNavState();

  const [fieldDefs, setFieldDefs] = useState();
  // The bars the selected Program's Tier ladders measure; empty unless one of them states more than one,
  // so the Threshold filter stays hidden for an ordinary single-bar Program.
  const [thresholdOptions, setThresholdOptions] = useState([]);
  const [formValues, setFormValues] = useNavStateSync('formValues');

  const today = dayjs().format('YYYYMMDD');
  const hasPrimarySelections =
    !!formValues?.periodStart && !!formValues?.custIds?.[0] && !!formValues?.progProdIds?.[0];

  // Program and Program Products are scoped to one Customer, so a Customer change must clear both rather
  // than carrying over a selection that may no longer make sense for the newly selected Customer.
  const prevCustIdRef = useRef(formValues?.custIds?.[0]);
  useEffect(() => {
    const custId = formValues?.custIds?.[0];
    if (prevCustIdRef.current !== undefined && custId !== prevCustIdRef.current) {
      setFormValues({ ...formValues, progId: undefined, progProdIds: undefined, periodStart: undefined });
      // Everything below the Customer stays disabled until the new Customer's filter data arrives
      setFieldDefs((defs) => defs?.map((def) => (def.field === 'custIds' ? def : disabledFieldDef(def))));
    }
    prevCustIdRef.current = custId;
  }, [formValues?.custIds]);

  // Program Products and Periods are defined by the Program, so a Program change invalidates both.
  const prevProgIdRef = useRef(formValues?.progId);
  useEffect(() => {
    const progId = formValues?.progId;
    if (prevProgIdRef.current !== progId) {
      if (formValues?.progProdIds !== undefined || formValues?.periodStart !== undefined) {
        setFormValues({ ...formValues, progProdIds: undefined, periodStart: undefined });
      }
      // Both stay disabled until the new Program's filter data arrives
      setFieldDefs((defs) =>
        defs?.map((def) => (def.field === 'progProdIds' || def.field === 'periodStart' ? disabledFieldDef(def) : def)),
      );
    }
    prevProgIdRef.current = progId;
  }, [formValues?.progId]);

  useEffect(() => {
    const custId = formValues?.custIds?.[0];
    const progId = formValues?.progId;
    // A Customer change clears the Program, so two requests can be in flight; only the latest may apply
    let stale = false;
    rsrc
      .data('md', 'TIER_DASHBOARD_FILTERS_DATA_PLUGIN', {
        custId,
        progId,
      })
      .then((res) => {
        if (stale) {
          return;
        }
        const periods = res.periods ?? [];
        setThresholdOptions(res.thresholds ?? []);
        setFieldDefs([
          { field: 'custIds', displayName: 'Customer', type: 'TREE', treeData: res.custTreeData, multi: false },
          // Each filter narrows the next: Programs come from the Customer, Products and Periods from the Program
          { field: 'progId', displayName: 'Program', type: 'DROPDOWN', values: res.progs, disabled: !custId },
          progProdsFieldDef(res.progProdTreeData, !progId),
          {
            field: 'periodStart',
            displayName: 'Period',
            type: 'DROPDOWN',
            values: periods,
            disabled: !progId,
          },
        ]);

        // Resolved against the latest values rather than those captured when the request was issued, so a
        // Product or Period picked while it was in flight is not overwritten by this response
        setFormValues((prev) => {
          const nextValues = { ...prev };
          let changed = false;
          // Program is left unselected until the user picks one; only a carry-over that no longer
          // belongs to this Customer's list is cleared
          if (progId && !res.progs.some((prog) => prog.value === progId)) {
            nextValues.progId = undefined;
            changed = true;
          }
          // Periods are defined by the Program, so a Program change invalidates a carried-over period
          if (!periods.length) {
            changed = changed || nextValues.periodStart !== undefined;
            nextValues.periodStart = undefined;
          } else if (!periods.map((period) => period.value).includes(nextValues.periodStart)) {
            nextValues.periodStart = defaultPeriodValue(periods, today);
            changed = true;
          }
          return changed ? nextValues : prev;
        });
      });
    return () => {
      stale = true;
    };
    // Only the Customer and Program narrow this request; Products and Period are derived from the response
  }, [formValues?.custIds, formValues?.progId]);

  const progProdFieldDef = fieldDefs?.find((f) => f.field === 'progProdIds');
  const selectedNodeIds = new Set(formValues?.progProdIds ?? []);
  const groupNodes = progProdFieldDef?.treeData?.children ?? [];
  // Member nodes are grandchildren of root (root -> Program Product -> member Program Product). Each member
  // leaf has a synthetic, per-node tree id (the same Program Product can be a member of several groups), so
  // the real Program Product ID for downstream queries is recovered from code rather than id. Kept as a
  // string - Longs travel as strings end-to-end since JS numbers can't hold them precisely.
  const memberNodes = groupNodes.flatMap((node) => (node.children?.length ? node.children : [node]));
  const selectedMembers = memberNodes
    .filter((memberNode) => selectedNodeIds.has(memberNode.id))
    .map((memberNode) => ({ id: memberNode.code, name: memberNode.name }));

  // Members can only be selected within a single group, so the progress shown always belongs to exactly
  // one Program Product - the one being priced - whose real ID rides in the group node's code.
  const selectedGroupNode = groupNodes.find((node) =>
    (node.children ?? []).some((memberNode) => selectedNodeIds.has(memberNode.id)),
  );
  const selectedProgProdId = selectedGroupNode?.code;

  // A Tier formula can state several bars that all have to be cleared, each over its own products. Only one
  // can be charted at a time, and since a bar is measured over exactly those products, choosing one selects
  // them - which also has to happen when the Products selection moves to another Program Product's ladder.
  const prevThresholdSelRef = useRef();
  useEffect(() => {
    const thresholdKey = formValues?.thresholdKey;
    if (thresholdOptions.length < 2) {
      prevThresholdSelRef.current = undefined;
      if (thresholdKey !== undefined) {
        setFormValues({ ...formValues, thresholdKey: undefined });
      }
      return;
    }
    const option = thresholdOptions.find((threshold) => threshold.value === thresholdKey);
    if (!option) {
      // The combined bar is the one the Program is sold on, and the options are ordered to end on it
      setFormValues({ ...formValues, thresholdKey: thresholdOptions[thresholdOptions.length - 1].value });
      return;
    }
    const selection = `${selectedGroupNode?.id ?? ''}|${thresholdKey}`;
    if (prevThresholdSelRef.current === selection) {
      return;
    }
    prevThresholdSelRef.current = selection;
    const nodeIds = (selectedGroupNode?.children ?? [])
      .filter((memberNode) => option.progProdIds.includes(memberNode.code))
      .map((memberNode) => memberNode.id);
    if (nodeIds.length) {
      setFormValues({ ...formValues, progProdIds: nodeIds });
    }
  }, [thresholdOptions, formValues?.thresholdKey, selectedGroupNode?.id]);

  if (!fieldDefs) {
    return;
  }

  const selectedPeriod = fieldDefs
    .find((f) => f.field === 'periodStart')
    ?.values?.find((period) => period.value === formValues?.periodStart);
  const isCurrentPeriod = !!selectedPeriod && selectedPeriod.value <= today && today <= selectedPeriod.endDate;
  // Every plugin resolves the Tier period from a single date, so the period is expressed as the day within
  // it that the figures are as of: today for the open period, the closing day for one already finished.
  const evalDate =
    !selectedPeriod ? undefined
    : isCurrentPeriod ? today
    : selectedPeriod.endDate;

  const hasAllSelections = hasPrimarySelections && !!formValues?.progId && !!selectedProgProdId;

  // Threshold only earns a place in the filters when the Program leaves a choice of bar to measure against
  const renderedFieldDefs =
    thresholdOptions.length < 2 ?
      fieldDefs
    : [...fieldDefs, { field: 'thresholdKey', displayName: 'Threshold', type: 'DROPDOWN', values: thresholdOptions }];

  // Price Res is written per Program Product as contiguous, non-overlapping Tier period segments, so the
  // selected period lands on exactly one row for the Program Product the selection is grouped under.
  const navigateToPriceRes = async (event) => {
    const date = Number(evalDate);
    const expr = ExprUtils.and(
      ExprUtils.is('progProdId', selectedProgProdId),
      ExprUtils.is('custId', formValues.custIds[0]),
      ExprUtils.isLessOrEqual('startDate', date),
      ExprUtils.isGreaterOrEqual('endDate', date),
    );

    const priceRes = await rsrc.one('md', 'PRICE_RES', { expr });
    if (priceRes) {
      navigateTo('/md/price-res', { mode: Mode.DETAIL, entityId: priceRes.id }, event.ctrlKey);
      return;
    }
    navigateTo('/md/price-res', { mode: Mode.LIST, expr }, event.ctrlKey);
  };

  return (
    <ContentBox>
      {/* Title */}

      <Title
        icon={fas.faTasks}
        title="Tier Dashboard"
        buttons={[
          <TitleButton
            key="priceResButton"
            icon={fas.faUsdCircle}
            toolTip="Price Results"
            disabled={!hasAllSelections}
            onClick={navigateToPriceRes}
          />,
        ]}
      />

      {/* Filters */}
      <Form formValues={formValues} setFormValues={setFormValues}>
        <FormFields fieldDefs={renderedFieldDefs} container={FilterContainer} />
      </Form>
      <SpacerLayout>
        {evalDate && formValues?.custIds?.[0] && selectedMembers.length > 0 && (
          <Spacer vertical weights={[1, 1]}>
            <Spacer weights={[2, 3]}>
              <KrCard title="Tier Progress">
                <ProgressChart
                  date={evalDate}
                  custId={formValues.custIds[0]}
                  progProdId={selectedProgProdId}
                  memberProgProdIds={selectedMembers.map((member) => member.id)}
                  thresholdKey={formValues?.thresholdKey}
                />
              </KrCard>

              <KrCard title="Eligible Sales By Month">
                <SaleChart
                  date={evalDate}
                  custId={formValues.custIds[0]}
                  progProdId={selectedProgProdId}
                  members={selectedMembers}
                  thresholdKey={formValues?.thresholdKey}
                />
              </KrCard>
            </Spacer>

            <Spacer weights={[4, 1]}>
              <KrCard title="Cumulative Eligible Sales">
                <CumulativeSaleChart
                  date={evalDate}
                  custId={formValues.custIds[0]}
                  progProdId={selectedProgProdId}
                  memberProgProdIds={selectedMembers.map((member) => member.id)}
                  thresholdKey={formValues?.thresholdKey}
                  markerDate={isCurrentPeriod ? today : undefined}
                />
              </KrCard>

              <RebateCards
                date={evalDate}
                custId={formValues.custIds[0]}
                progProdId={selectedProgProdId}
                showProjected={isCurrentPeriod}
              />
            </Spacer>
          </Spacer>
        )}
      </SpacerLayout>
    </ContentBox>
  );
};
